/**
 * 编辑器本机调试连接：只存在这台电脑，按工程/场景 id 分桶，不进 git、不进 .f3p。
 * 依据：docs/05-连接数据.md §2 / §4.5 / §5。
 */
import { defineStore } from 'pinia'
import {
  LOCAL_CONNECTION_STORAGE_PREFIX,
  type LocalConnectionConfig,
} from '@/types/connectData'

export const DEFAULT_LOCAL_CONNECTION: LocalConnectionConfig = {
  baseUrl: '',
  username: '',
  password: '',
  organizationId: '',
  pollIntervalMs: 3000,
}

export function localConnectionStorageKey(sceneOrProjectId: string): string {
  return `${LOCAL_CONNECTION_STORAGE_PREFIX}${sceneOrProjectId}`
}

function stripTrailingSlash(baseUrl: string): string {
  return baseUrl.trim().replace(/\/+$/, '')
}

export function loadLocalConnection(sceneOrProjectId: string): LocalConnectionConfig | null {
  try {
    const raw = localStorage.getItem(localConnectionStorageKey(sceneOrProjectId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LocalConnectionConfig>
    return {
      baseUrl: typeof parsed.baseUrl === 'string' ? stripTrailingSlash(parsed.baseUrl) : '',
      username: typeof parsed.username === 'string' ? parsed.username : '',
      password: typeof parsed.password === 'string' ? parsed.password : '',
      organizationId: typeof parsed.organizationId === 'string' ? parsed.organizationId : '',
      pollIntervalMs: typeof parsed.pollIntervalMs === 'number' ? parsed.pollIntervalMs : 3000,
    }
  } catch {
    return null
  }
}

export function saveLocalConnection(sceneOrProjectId: string, config: LocalConnectionConfig): void {
  const toSave: LocalConnectionConfig = {
    ...config,
    baseUrl: stripTrailingSlash(config.baseUrl),
  }
  localStorage.setItem(localConnectionStorageKey(sceneOrProjectId), JSON.stringify(toSave))
}

export function clearLocalConnection(sceneOrProjectId: string): void {
  localStorage.removeItem(localConnectionStorageKey(sceneOrProjectId))
}

export const useLocalConnectionStore = defineStore('localConnection', () => {
  function load(sceneOrProjectId: string): LocalConnectionConfig {
    return loadLocalConnection(sceneOrProjectId) ?? { ...DEFAULT_LOCAL_CONNECTION }
  }

  function save(sceneOrProjectId: string, config: LocalConnectionConfig): void {
    saveLocalConnection(sceneOrProjectId, config)
  }

  function clear(sceneOrProjectId: string): void {
    clearLocalConnection(sceneOrProjectId)
  }

  return { load, save, clear }
})
